import React from 'react'
import * as stores from '../stores/appStore'
import * as types from '../types'

const CalendarView: React.FC = () => {
  const { people, selectedPeople } = stores.useAppStore()
  const [currentDate, setCurrentDate] = React.useState(new Date())
  const [viewType, setViewType] = React.useState<'week' | 'month'>('week')
  const [expandedCells, setExpandedCells] = React.useState<Set<string>>(new Set())

  // Get the start of the week (Sunday)
  const getWeekStart = (date: Date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day
    return new Date(d.setDate(diff))
  }

  // Get the start of the month
  const getMonthStart = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  // Get all dates for the current view
  const getViewDates = () => {
    if (viewType === 'week') {
      const start = getWeekStart(currentDate)
      const dates = []
      for (let i = 0; i < 7; i++) {
        const date = new Date(start)
        date.setDate(start.getDate() + i)
        dates.push(date)
      }
      return dates
    } else {
      // Month view
      const start = getMonthStart(currentDate)
      const startWeek = getWeekStart(start)
      const dates = []

      // Get 6 weeks (42 days) to cover any month layout
      for (let i = 0; i < 42; i++) {
        const date = new Date(startWeek)
        date.setDate(startWeek.getDate() + i)
        dates.push(date)
      }
      return dates
    }
  }

  const viewDates = getViewDates()

  // Format date for comparison with slot dates
  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0]
  }

  // Extract calendar info from URL
  const parseCalendarUrl = (url: string) => {
    // Format: https://calendly.com/username/event-slug
    const match = url.match(/calendly\.com\/([^\/]+)\/([^\/\?]+)/)
    if (match) {
      return { username: match[1], eventSlug: match[2] }
    }
    // For profile URLs that were expanded
    const profileMatch = url.match(/calendly\.com\/([^\/\?]+)/)
    if (profileMatch) {
      return { username: profileMatch[1], eventSlug: null }
    }
    return null
  }

  // Generate booking URL
  const generateBookingUrl = (calendar: types.Calendar, slot: types.TimeSlot) => {
    const parsed = parseCalendarUrl(calendar.url)
    if (!parsed) return null

    const startTime = slot.start_time
    const date = slot.date
    const month = date.substring(0, 7) // YYYY-MM

    // If we have the full URL with event slug
    if (parsed.eventSlug) {
      return `https://calendly.com/${parsed.username}/${parsed.eventSlug}/${startTime}?month=${month}&date=${date}`
    }

    // Fallback to just the calendar URL
    return calendar.url
  }

  // Get duration from calendar info
  const getDuration = (calendar: types.Calendar) => {
    // Try to extract duration from calendar name
    if (calendar.name) {
      const match = calendar.name.match(/(\d+)\s*min/i)
      if (match && match[1]) {
        return parseInt(match[1])
      }
    }
    return 30 // Default
  }

  // Get all slots for a specific date from a specific person
  const getSlotsForDateAndPerson = (date: string, personId: string) => {
    const slots: Array<{
      person: types.Person
      calendar: types.Calendar
      slot: types.TimeSlot
      duration: number
    }> = []

    const person = people.find(p => p.id === personId)
    if (!person) return slots

    for (const calendar of person.calendars) {
      if (!calendar.enabled || !calendar.cachedAvailability) continue

      const daySlots = calendar.cachedAvailability.slots.filter(
        slot => slot.date === date
      )

      for (const slot of daySlots) {
        slots.push({
          person,
          calendar,
          slot,
          duration: getDuration(calendar)
        })
      }
    }

    // Sort by time
    return slots.sort((a, b) => {
      const timeA = new Date(a.slot.start_time).getTime()
      const timeB = new Date(b.slot.start_time).getTime()
      return timeA - timeB
    })
  }


  // Get people to display (selected or all)
  const peopleToShow = React.useMemo(() => {
    return selectedPeople.length > 0
      ? people.filter(p => selectedPeople.includes(p.id))
      : people
  }, [people, selectedPeople])

  // Navigate dates
  const goToPrevious = () => {
    if (viewType === 'week') {
      const prev = new Date(currentDate)
      prev.setDate(prev.getDate() - 7)
      setCurrentDate(prev)
    } else {
      const prev = new Date(currentDate)
      prev.setMonth(prev.getMonth() - 1)
      setCurrentDate(prev)
    }
  }

  const goToNext = () => {
    if (viewType === 'week') {
      const next = new Date(currentDate)
      next.setDate(next.getDate() + 7)
      setCurrentDate(next)
    } else {
      const next = new Date(currentDate)
      next.setMonth(next.getMonth() + 1)
      setCurrentDate(next)
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Calculate total available slots
  const totalSlots = React.useMemo(() => {
    let count = 0
    for (const date of viewDates) {
      const dateKey = formatDateKey(date)
      for (const person of peopleToShow) {
        count += getSlotsForDateAndPerson(dateKey, person.id!).length
      }
    }
    return count
  }, [viewDates, peopleToShow])

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth()
  }

  const handleSlotClick = (calendar: types.Calendar, slot: types.TimeSlot) => {
    const bookingUrl = generateBookingUrl(calendar, slot)
    if (bookingUrl) {
      window.open(bookingUrl, '_blank')
    }
  }

  const toggleCellExpansion = (cellKey: string) => {
    const newExpanded = new Set(expandedCells)
    if (newExpanded.has(cellKey)) {
      newExpanded.delete(cellKey)
    } else {
      newExpanded.add(cellKey)
    }
    setExpandedCells(newExpanded)
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* People Selector at Top */}
      {people.length > 0 && (
        <div className="mb-6 pb-4 border-b">
          <h3 className="text-sm font-semibold mb-2">Select People to View</h3>
          <div className="flex flex-wrap gap-3">
            {people.map(person => {
              const slotCount = person.calendars.reduce((acc, cal) =>
                acc + (cal.cachedAvailability?.slots.length || 0), 0
              )

              return (
                <button
                  key={person.id}
                  onClick={() => stores.useAppStore.getState().togglePersonSelection(person.id!)}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-lg border transition-all ${
                    selectedPeople.includes(person.id!) || selectedPeople.length === 0
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 opacity-50'
                  }`}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: person.color }}
                  />
                  <span className="text-sm">
                    {person.firstName} {person.lastName}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({slotCount})
                  </span>
                </button>
              )
            })}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Click on people to show/hide their availability • Click on slots below to book directly
          </div>
        </div>
      )}

      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Calendar View</h2>
        <div className="flex items-center space-x-4">
          {/* View type toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewType('week')}
              className={`px-3 py-1 text-sm rounded ${
                viewType === 'week'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewType('month')}
              className={`px-3 py-1 text-sm rounded ${
                viewType === 'month'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600'
              }`}
            >
              Month
            </button>
          </div>

          {/* Navigation buttons */}
          <button
            onClick={goToPrevious}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200"
          >
            Today
          </button>
          <button
            onClick={goToNext}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Date info */}
      <div className="mb-4 text-sm text-gray-600">
        {viewType === 'week' && viewDates.length >= 7
          ? `Week of ${viewDates[0]?.toLocaleDateString()} - ${viewDates[6]?.toLocaleDateString()}`
          : currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        }
        {totalSlots > 0 && (
          <span className="ml-4 font-semibold text-green-600">
            {totalSlots} available slots
          </span>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        {peopleToShow.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No people selected. Use the legend below to select people to view.
          </div>
        ) : peopleToShow.length === 1 ? (
          // Single person view - original grid layout
          <div className={viewType === 'month' ? 'min-w-[900px]' : 'min-w-[800px]'}>
            {/* Day headers */}
            <div className={`grid ${viewType === 'month' ? 'grid-cols-7' : 'grid-cols-7'} gap-2 mb-4`}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-semibold text-gray-600">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar cells for single person */}
            <div className={`grid ${viewType === 'month' ? 'grid-cols-7' : 'grid-cols-7'} gap-2`}>
              {viewDates.map((date, idx) => {
                const dateKey = formatDateKey(date)
                const firstPerson = peopleToShow[0]
                const slots = firstPerson ? getSlotsForDateAndPerson(dateKey, firstPerson.id!) : []
                const isToday = formatDateKey(date) === formatDateKey(new Date())
                const inCurrentMonth = viewType === 'month' ? isCurrentMonth(date) : true
                const cellKey = `single-${idx}`
                const isExpanded = expandedCells.has(cellKey)

                return (
                  <div
                    key={idx}
                    className={`border rounded-lg p-2 ${
                      viewType === 'month' ? 'min-h-[120px]' : 'min-h-[200px]'
                    } ${
                      inCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${
                      isToday ? 'border-primary-500 ring-1 ring-primary-200' : 'border-gray-200'
                    }`}
                  >
                    {/* Date number */}
                    <div className={`text-sm font-semibold mb-1 ${
                      !inCurrentMonth ? 'text-gray-400' :
                      isToday ? 'text-primary-600' : 'text-gray-700'
                    }`}>
                      {date.getDate()}
                    </div>

                    {/* Slots by calendar (tracks) */}
                    {slots.length > 0 ? (
                      <div className="space-y-2">
                        {/* Group by calendar for tracks */}
                        {Object.entries(
                          slots.reduce((acc, slot) => {
                            const calKey = slot.calendar.id
                            if (!acc[calKey]) acc[calKey] = []
                            acc[calKey]!.push(slot)
                            return acc
                          }, {} as Record<string, typeof slots>)
                        )
                          .slice(0, viewType === 'month' ? 2 : 3)
                          .filter(([, calSlots]) => calSlots && calSlots.length > 0)
                          .map(([calId, calSlots]) => {
                            if (!calSlots || calSlots.length === 0) return null
                            const calendar = calSlots[0]?.calendar
                            if (!calendar) return null
                            return (
                              <div key={calId} className="space-y-1">
                                {viewType === 'week' && (
                                  <div className="text-xs text-gray-500 truncate">
                                    {calendar.name}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {calSlots.slice(0, isExpanded ? undefined : (viewType === 'month' ? 2 : 3)).map((slot, idx) => {
                                    const displayTime = new Date(slot.slot.start_time).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                    return (
                                      <button
                                        key={idx}
                                        onClick={() => handleSlotClick(slot.calendar, slot.slot)}
                                        className="px-1.5 py-0.5 rounded text-xs hover:bg-gray-100 transition-colors cursor-pointer"
                                        style={{
                                          backgroundColor: `${slot.person.color}15`,
                                          border: `1px solid ${slot.person.color}`
                                        }}
                                        title={`${calendar.name}\n${displayTime}\nClick to book`}
                                      >
                                        {displayTime}
                                      </button>
                                    )
                                  })}
                                  {!isExpanded && calSlots.length > (viewType === 'month' ? 2 : 3) && (
                                    <button
                                      onClick={() => toggleCellExpansion(cellKey)}
                                      className="text-xs text-primary-600 hover:text-primary-700 italic underline"
                                    >
                                      +{calSlots.length - (viewType === 'month' ? 2 : 3)} more
                                    </button>
                                  )}
                                  {isExpanded && calSlots.length > (viewType === 'month' ? 2 : 3) && (
                                    <button
                                      onClick={() => toggleCellExpansion(cellKey)}
                                      className="text-xs text-gray-500 hover:text-gray-700 italic"
                                    >
                                      Show less
                                    </button>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    ) : (
                      inCurrentMonth && (
                        <div className="text-xs text-gray-400 text-center mt-4">
                          No slots
                        </div>
                      )
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          // Multiple people view - column layout
          <div className="min-w-[1200px]">
            {/* Header with dates and people */}
            <div className="flex">
              {/* Dates column */}
              <div className="w-24 flex-shrink-0">
                <div className="h-10 border-b border-gray-200"></div>
                {viewDates.filter((_, idx) => viewType === 'month' || idx < 7).map((date, idx) => {
                  const isToday = formatDateKey(date) === formatDateKey(new Date())
                  const inCurrentMonth = viewType === 'month' ? isCurrentMonth(date) : true
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
                  return (
                    <div
                      key={idx}
                      className={`h-24 border-b border-gray-200 p-2 ${
                        isToday ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className={`text-sm font-semibold ${
                        !inCurrentMonth ? 'text-gray-400' :
                        isToday ? 'text-primary-600' : 'text-gray-700'
                      }`}>
                        {dayName}
                      </div>
                      <div className={`text-lg ${
                        !inCurrentMonth ? 'text-gray-400' :
                        isToday ? 'text-primary-600' : 'text-gray-900'
                      }`}>
                        {date.getDate()}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* People columns */}
              {peopleToShow.map(person => (
                <div key={person.id} className="flex-1 min-w-[200px]">
                  {/* Person header */}
                  <div
                    className="h-10 border-b border-l border-gray-200 p-2 flex items-center"
                    style={{ backgroundColor: `${person.color}10` }}
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: person.color }}
                    />
                    <span className="font-semibold text-sm">
                      {person.firstName} {person.lastName?.[0]}.
                    </span>
                  </div>

                  {/* Person's slots for each date */}
                  {viewDates.filter((_, idx) => viewType === 'month' || idx < 7).map((date, dateIdx) => {
                    const dateKey = formatDateKey(date)
                    const slots = getSlotsForDateAndPerson(dateKey, person.id!)
                    const isToday = formatDateKey(date) === formatDateKey(new Date())
                    const cellKey = `col-${person.id}-${dateIdx}`
                    const isExpanded = expandedCells.has(cellKey)

                    return (
                      <div
                        key={dateIdx}
                        className={`h-24 border-b border-l border-gray-200 p-1 overflow-y-auto ${
                          isToday ? 'bg-primary-50' : 'bg-white'
                        }`}
                      >
                        {slots.length > 0 ? (
                          <div className="space-y-0.5">
                            {/* Group by calendar */}
                            {Object.entries(
                              slots.reduce((acc, slot) => {
                                const calKey = slot.calendar.id
                                if (!acc[calKey]) acc[calKey] = []
                                acc[calKey]!.push(slot)
                                return acc
                              }, {} as Record<string, typeof slots>)
                            )
                            .filter(([, calSlots]) => calSlots && calSlots.length > 0)
                            .map(([calId, calSlots]) => {
                              if (!calSlots || calSlots.length === 0) return null
                              const calendar = calSlots[0]?.calendar
                              if (!calendar) return null
                              return (
                                <div key={calId}>
                                  <div className="text-xs text-gray-500 truncate mb-0.5">
                                    {calendar.name}
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {calSlots.slice(0, isExpanded ? undefined : 3).map((slot, idx) => {
                                      const displayTime = new Date(slot.slot.start_time).toLocaleTimeString([], {
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() => handleSlotClick(slot.calendar, slot.slot)}
                                          className="px-1 py-0.5 rounded text-xs hover:opacity-80 transition-opacity cursor-pointer text-white"
                                          style={{
                                            backgroundColor: person.color
                                          }}
                                          title={`${calendar.name}\n${displayTime}\nClick to book`}
                                        >
                                          {displayTime}
                                        </button>
                                      )
                                    })}
                                    {!isExpanded && calSlots.length > 3 && (
                                      <button
                                        onClick={() => toggleCellExpansion(cellKey)}
                                        className="text-xs text-primary-600 hover:text-primary-700"
                                      >
                                        +{calSlots.length - 3}
                                      </button>
                                    )}
                                    {isExpanded && calSlots.length > 3 && (
                                      <button
                                        onClick={() => toggleCellExpansion(cellKey)}
                                        className="text-xs text-gray-500 hover:text-gray-700"
                                      >
                                        Less
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 text-center mt-6">
                            -
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default CalendarView